// ==============================|| SHIFT TIMING CALCULATOR ||============================== //

/**
 * Calculate shift timings based on store working hours and number of shifts
 * @param {string} startTime - Store opening time (HH:MM format)
 * @param {string} endTime - Store closing time (HH:MM format)
 * @param {number} numShifts - Number of shifts to divide the working hours
 * @returns {Array} Array of shift objects with start and end times
 */
export const calculateShiftTimings = (startTime, endTime, numShifts) => {
  if (!startTime || !endTime || !numShifts || numShifts < 1) {
    return [];
  }

  // Parse start and end times
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  // Convert to total minutes for easier calculation
  const startMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;

  // Calculate total working minutes
  const totalWorkingMinutes = endMinutes - startMinutes;

  // Calculate minutes per shift
  const minutesPerShift = Math.floor(totalWorkingMinutes / numShifts);

  const shifts = [];

  for (let i = 0; i < numShifts; i++) {
    const shiftStartMinutes = startMinutes + (i * minutesPerShift);
    const shiftEndMinutes = i === numShifts - 1 ? endMinutes : startMinutes + ((i + 1) * minutesPerShift);

    // Convert back to HH:MM format
    const shiftStartHour = Math.floor(shiftStartMinutes / 60);
    const shiftStartMinute = shiftStartMinutes % 60;
    const shiftEndHour = Math.floor(shiftEndMinutes / 60);
    const shiftEndMinute = shiftEndMinutes % 60;

    const shiftStart = `${shiftStartHour.toString().padStart(2, '0')}:${shiftStartMinute.toString().padStart(2, '0')}`;
    const shiftEnd = `${shiftEndHour.toString().padStart(2, '0')}:${shiftEndMinute.toString().padStart(2, '0')}`;

    shifts.push({
      id: i + 1,
      name: `Shift ${i + 1}`,
      startTime: shiftStart,
      endTime: shiftEnd,
      duration: `${Math.floor((shiftEndMinutes - shiftStartMinutes) / 60)}h ${(shiftEndMinutes - shiftStartMinutes) % 60}m`
    });
  }

  return shifts;
};

/**
 * Format time for display
 * @param {string} time - Time in HH:MM format
 * @returns {string} Formatted time (e.g., "9:00 AM")
 */
export const formatTimeForDisplay = (time) => {
  if (!time) return '';
  
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

/**
 * Calculate shift duration in hours
 * @param {string} startTime - Shift start time
 * @param {string} endTime - Shift end time
 * @returns {number} Duration in hours
 */
export const calculateShiftDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  
  const startMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;
  
  return (endMinutes - startMinutes) / 60;
}; 